import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTheme, type Theme, alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import apiService, {
  type ReviewAuditSummary,
  type ReviewTimeByPodcastRow,
} from "../services/api";

const DEFAULT_LOOKBACK_DAYS = 21; // 3 publish weeks (e.g. Jun 22, Jun 29, Jul 6)

/** Fixed manual review-time estimates (hours) — keyed by ISO week id. */
const FIXED_WEEKLY_REVIEW_HOURS: Record<string, number> = {
  "2026-W26": 9,
  "2026-W27": 7,
  "2026-W28": 6,
};

const FIXED_WEEKLY_REVIEW_HOURS_LABEL: Record<string, string> = {
  "2026-W26": "8–10 h",
  "2026-W27": "7 h",
  "2026-W28": "6 h",
};

function fixedReviewHoursForWeek(week: string): number | null {
  const hours = FIXED_WEEKLY_REVIEW_HOURS[week];
  return hours != null ? hours : null;
}

function fixedReviewHoursLabel(week: string): string {
  return FIXED_WEEKLY_REVIEW_HOURS_LABEL[week] ?? "—";
}

/** Round tracked time to the same number the bar label prints. */
function trackedReviewHours(raw: number): { hours: number; label: string } {
  if (raw < 10) {
    const hours = Math.round(raw * 10) / 10;
    return { hours, label: `${hours.toFixed(1)} h` };
  }
  const hours = Math.round(raw);
  return { hours, label: `${hours} h` };
}

/** Prefer tracked timer hours; fall back to fixed manual estimates for historical weeks. */
function reviewHoursForWeek(
  week: string,
  trackedByWeek: Record<string, number> | undefined,
): { hours: number | null; label: string; source: "tracked" | "fixed" | null } {
  const tracked = trackedByWeek?.[week];
  if (tracked != null) {
    const rounded = trackedReviewHours(tracked);
    return {
      hours: rounded.hours,
      label: rounded.label,
      source: "tracked",
    };
  }
  const fixed = fixedReviewHoursForWeek(week);
  if (fixed != null) {
    return {
      hours: fixed,
      label: fixedReviewHoursLabel(week),
      source: "fixed",
    };
  }
  return { hours: null, label: "—", source: null };
}

type HotspotSortKey = "title" | "total" | "rejected" | "edited" | "added" | "cleanAccept" | "precision";

type HotspotRow = {
  podcastId: string;
  title: string;
  rejected: number;
  added: number;
  edited: number;
  cleanAccept: number;
  total: number;
  precision: number | null;
};

type HotspotContentRow = {
  contentId: string;
  title: string;
  rejected: number;
  added: number;
  edited: number;
  cleanAccept: number;
  total: number;
  precision: number | null;
  whisperCostUsd: number | null;
  sieve4bCostUsd: number | null;
  validate32bCostUsd: number | null;
  shortQwenCostUsd: number | null;
  loupeQwenCostUsd: number | null;
  gptCostUsd: number | null;
  geminiCostUsd: number | null;
};

/** Higher is worse for volume metrics; lower is worse for cleanAccept/precision. */
const HOTSPOT_WORST_HIGHER: Record<Exclude<HotspotSortKey, "title">, boolean> = {
  total: true,
  rejected: true,
  edited: true,
  added: true,
  cleanAccept: false,
  precision: false,
};

function topWorstContentsForPodcast(
  rows: HotspotContentRow[],
  sortKey: HotspotSortKey,
  limit = 10,
): HotspotContentRow[] {
  const metric: Exclude<HotspotSortKey, "title"> = sortKey === "title" ? "total" : sortKey;
  const higherIsWorse = HOTSPOT_WORST_HIGHER[metric];
  const sorted = [...rows].sort((a, b) => {
    if (metric === "precision") {
      const av = a.precision ?? (higherIsWorse ? -1 : Number.POSITIVE_INFINITY);
      const bv = b.precision ?? (higherIsWorse ? -1 : Number.POSITIVE_INFINITY);
      return higherIsWorse ? bv - av : av - bv;
    }
    const av = a[metric];
    const bv = b[metric];
    return higherIsWorse ? bv - av : av - bv;
  });
  return sorted.slice(0, limit);
}

function ymdDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const pct = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`;

function formatTokens(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(n));
}

function formatUsd(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 100) return `$${n.toFixed(0)}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n > 0) return `$${n.toFixed(3)}`;
  return "$0";
}

function formatGpuHours(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n >= 10) return `${n.toFixed(1)} h`;
  return `${n.toFixed(2)} h`;
}

/** Human-readable GPU processing duration from total seconds (prefer hours for week totals). */
function formatProcessingDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return "—";
  const hours = seconds / 3600;
  if (hours < 1) {
    const mins = Math.round(seconds / 60);
    return mins < 1 ? `${Math.round(seconds)}s` : `${mins} min`;
  }
  return `${hours.toFixed(1)} h`;
}

const EMPTY_AI_TOKEN = {
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
  estimatedCostUsd: 0,
};

const EMPTY_RUNPOD = {
  processingDurationSeconds: 0,
  gpuHours: 0,
  estimatedCostUsd: 0,
};

const BUCKET_META: Array<{
  key: "rejected" | "added" | "edited" | "cleanAccept";
  label: string;
  color: (t: Theme) => string;
}> = [
  { key: "edited", label: "Edited (field corrections)", color: (t) => t.palette.warning.main },
  { key: "added", label: "Added (human-caught misses)", color: (t) => t.palette.info.main },
  { key: "rejected", label: "Rejected (false positives)", color: (t) => t.palette.error.main },
  { key: "cleanAccept", label: "Clean accept (one-click confirm)", color: (t) => t.palette.success.main },
];

function StatCard({
  value,
  label,
  color,
  tooltip,
}: {
  value: React.ReactNode;
  label: string;
  color?: string;
  tooltip?: string;
}) {
  const card = (
    <Paper
      variant="outlined"
      sx={{ p: 2, height: "100%", cursor: tooltip ? "help" : "default" }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={
          tooltip
            ? { textDecoration: "underline dotted", textUnderlineOffset: 3 }
            : undefined
        }
      >
        {label}
      </Typography>
    </Paper>
  );
  return tooltip ? (
    <MuiTooltip title={tooltip} arrow placement="top">
      {card}
    </MuiTooltip>
  ) : (
    card
  );
}

function SectionTitle({ children, caption }: { children: React.ReactNode; caption?: string }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
      {caption && (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  );
}

const REVIEW_TIME_WEEK_OPTIONS = 9;

function formatReviewMinutes(ms: number): string {
  const minutes = ms / 60_000;
  return `${minutes < 10 ? minutes.toFixed(1) : Math.round(minutes).toLocaleString()} min`;
}

function formatMinutesPerHour(minutes: number): string {
  return minutes < 10 ? minutes.toFixed(1) : String(Math.round(minutes));
}

function isoWeekKeyForDate(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function previousWeekKeys(weekKey: string, count: number): string[] {
  const monday = isoWeekStartDate(weekKey);
  if (!monday) return [weekKey];
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() - i * 7);
    return isoWeekKeyForDate(d);
  });
}

type ReviewTimeSortKey =
  | "title"
  | "totalMs"
  | "perHour"
  | "share"
  | "contentSeconds"
  | "mentionsReviewed"
  | "mentionsAccepted";

function reviewMinutesPerContentHour(p: ReviewTimeByPodcastRow): number | null {
  return p.contentSeconds > 0 ? p.totalMs / 60_000 / (p.contentSeconds / 3600) : null;
}

function reviewTimeSortValue(p: ReviewTimeByPodcastRow, key: ReviewTimeSortKey): string | number | null {
  switch (key) {
    case "title":
      return (p.title ?? p.podcastId).toLowerCase();
    case "perHour":
      return reviewMinutesPerContentHour(p);
    case "share":
      return p.totalMs;
    default:
      return p[key];
  }
}

const REVIEW_TIME_SORT_COLUMNS: Array<{ key: ReviewTimeSortKey; label: string; tooltip?: string }> = [
  { key: "title", label: "Podcast" },
  { key: "totalMs", label: "Review time" },
  {
    key: "perHour",
    label: "Per content hour",
    tooltip: "Minutes of tracked review per hour of content marked reviewed this week",
  },
  { key: "share", label: "Share of week" },
  { key: "contentSeconds", label: "Content hours" },
  { key: "mentionsReviewed", label: "Mentions reviewed" },
  { key: "mentionsAccepted", label: "Mentions accepted" },
];

function ReviewTimeByPodcastSection() {
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<ReviewTimeSortKey>("totalMs");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data, isLoading, error } = useQuery({
    queryKey: ["reviewTimeByPodcast", week ?? "current"],
    queryFn: () => apiService.getReviewTimeByPodcast(week),
  });
  const { data: current } = useQuery({
    queryKey: ["reviewTimeByPodcast", "current"],
    queryFn: () => apiService.getReviewTimeByPodcast(),
    enabled: week != null,
  });
  const currentWeekKey = week == null ? data?.weekKey : current?.weekKey;
  const weekOptions = currentWeekKey
    ? previousWeekKeys(currentWeekKey, REVIEW_TIME_WEEK_OPTIONS)
    : [];

  const sortedPodcasts = useMemo(() => {
    const rows = [...(data?.podcasts ?? [])];
    rows.sort((a, b) => {
      const av = reviewTimeSortValue(a, sortKey);
      const bv = reviewTimeSortValue(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === "string" ? av.localeCompare(String(bv)) : av - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data?.podcasts, sortKey, sortDir]);
  const pageRows = sortedPodcasts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key: ReviewTimeSortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "title" ? "asc" : "desc");
    }
    setPage(0);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2}>
        <SectionTitle caption="Tracked review timer hours per podcast (all reviewers), credited to the podcast open in the focused /review tab. Content hours = episodes marked reviewed this week; mentions accepted = AI mentions kept (with or without edits).">
          Review time by podcast
        </SectionTitle>
        {weekOptions.length > 0 && (
          <TextField
            select
            size="small"
            label="Week"
            value={data?.weekKey ?? weekOptions[0]}
            onChange={(e) => {
              setExpanded(null);
              setPage(0);
              setWeek(e.target.value === weekOptions[0] ? undefined : e.target.value);
            }}
            sx={{ minWidth: 180 }}
          >
            {weekOptions.map((k) => (
              <MenuItem key={k} value={k}>
                {k} · {formatIsoWeekStart(k)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error || !data ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to load review time by podcast.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {REVIEW_TIME_SORT_COLUMNS.map((col) => {
                  const active = col.key === sortKey;
                  const label = (
                    <TableSortLabel
                      active={active}
                      direction={active ? sortDir : col.key === "title" ? "asc" : "desc"}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  );
                  return (
                    <TableCell
                      key={col.key}
                      align={col.key === "title" ? "left" : "right"}
                      sortDirection={active ? sortDir : false}
                    >
                      {col.tooltip ? <MuiTooltip title={col.tooltip}>{label}</MuiTooltip> : label}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.podcasts.length === 0 && data.untrackedMs === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      No tracked review time this week.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map((p) => {
                const open = expanded === p.podcastId;
                return (
                  <React.Fragment key={p.podcastId}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => setExpanded(open ? null : p.podcastId)}
                          aria-label={open ? "Hide reviewers" : "Show reviewers"}
                        >
                          {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar src={p.imageUrl ?? undefined} variant="rounded" sx={{ width: 28, height: 28 }}>
                            {(p.title ?? "?").charAt(0)}
                          </Avatar>
                          <Typography variant="body2">{p.title ?? p.podcastId}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatReviewMinutes(p.totalMs)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const perHour = reviewMinutesPerContentHour(p);
                          return perHour == null ? "—" : `${formatMinutesPerHour(perHour)} min/h`;
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {pct(data.totalMs > 0 ? p.totalMs / data.totalMs : null)}
                      </TableCell>
                      <TableCell align="right">
                        {p.episodesReviewed > 0 ? (
                          <MuiTooltip title={`${p.episodesReviewed} episode${p.episodesReviewed === 1 ? "" : "s"} marked reviewed`}>
                            <span>{formatAnalyzedHours(p.contentSeconds / 3600)}</span>
                          </MuiTooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">{p.mentionsReviewed.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {p.mentionsAccepted.toLocaleString()}
                        {p.mentionsReviewed > 0 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({pct(p.mentionsAccepted / p.mentionsReviewed)})
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Table size="small" sx={{ my: 1 }}>
                            <TableBody>
                              {p.byUser.map((u) => (
                                <TableRow key={u.userId}>
                                  <TableCell sx={{ pl: 8 }}>{u.name}</TableCell>
                                  <TableCell align="right">{formatReviewMinutes(u.totalMs)}</TableCell>
                                  <TableCell align="right">{pct(p.totalMs > 0 ? u.totalMs / p.totalMs : null)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {data.untrackedMs > 0 && (
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      No podcast open
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatReviewMinutes(data.untrackedMs)}</TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {pct(data.totalMs > 0 ? data.untrackedMs / data.totalMs : null)}
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {sortedPodcasts.length > 0 && (
            <TablePagination
              component="div"
              count={sortedPodcasts.length}
              page={page}
              onPageChange={(_e, next) => {
                setExpanded(null);
                setPage(next);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </TableContainer>
      )}
    </Box>
  );
}

type WeekRow = ReviewAuditSummary["quality"]["weekly"][number];

function formatAnalyzedHours(hours: number): string {
  return hours >= 100 ? `${Math.round(hours)} h` : `${hours.toFixed(1)} h`;
}

const PIPELINE_LABELS = { loupe: "Loupe", whisper: "Whisper only" } as const;

/**
 * Review time is tracked per week, not per episode — split it by each pipeline's
 * share of mentions, so the review figures are estimates.
 */
function pipelinePerHourItems(
  byPipeline: WeekRow["byPipeline"],
  weekReviewHours: number,
  colors: Record<"loupe" | "whisper", string>,
): Array<{ name: string; swatchColor: string; formatted: string }> {
  if (!byPipeline) return [];
  const totalMentions = byPipeline.loupe.mentions + byPipeline.whisper.mentions;
  return (["loupe", "whisper"] as const).flatMap((key) => {
    const p = byPipeline[key];
    if (!(p.analyzedHours > 0)) return [];
    const label = PIPELINE_LABELS[key];
    const items = [
      {
        name: `${label} · AI cost`,
        swatchColor: colors[key],
        formatted: `${formatUsd(p.aiCostUsd / p.analyzedHours)} (${formatAnalyzedHours(p.analyzedHours)})`,
      },
    ];
    if (weekReviewHours > 0 && totalMentions > 0) {
      const reviewHours = (weekReviewHours * p.mentions) / totalMentions;
      items.push({
        name: `${label} · review time`,
        swatchColor: colors[key],
        formatted: `${((reviewHours * 60) / p.analyzedHours).toFixed(1)} min (est.)`,
      });
    }
    return items;
  });
}

/** Guard against older API responses missing newer fields (e.g. weekly). */
function withReviewAuditDefaults(data: ReviewAuditSummary): ReviewAuditSummary {
  return {
    ...data,
    effort: {
      ...data.effort,
      actions: data.effort.actions ?? {
        rejected: 0,
        added: 0,
        edited: 0,
        cleanAccept: 0,
      },
      byPlacement: data.effort.byPlacement ?? [],
      byFlow: data.effort.byFlow ?? [],
      rejectReasons: data.effort.rejectReasons ?? [],
      rejectLeverage: data.effort.rejectLeverage ?? [],
      editedFields: data.effort.editedFields ?? [],
      perPodcast: data.effort.perPodcast ?? [],
      perContent: data.effort.perContent ?? [],
    },
    quality: {
      ...data.quality,
      missing: data.quality.missing ?? 0,
      weekly: data.quality.weekly ?? [],
      flows: data.quality.flows ?? [],
      leverage: data.quality.leverage ?? [],
      editedFields: data.quality.editedFields ?? [],
      perPodcast: data.quality.perPodcast ?? [],
      perContent: data.quality.perContent ?? [],
    },
    aiSpend: {
      episodesInQuality: data.aiSpend?.episodesInQuality ?? data.quality.episodes ?? 0,
      episodesWithUsage: data.aiSpend?.episodesWithUsage ?? 0,
      gpt: data.aiSpend?.gpt ?? { ...EMPTY_AI_TOKEN },
      gemini: data.aiSpend?.gemini ?? { ...EMPTY_AI_TOKEN },
      runpod: data.aiSpend?.runpod ?? { ...EMPTY_RUNPOD },
      whisper: data.aiSpend?.whisper ?? data.aiSpend?.runpod ?? { ...EMPTY_RUNPOD },
      sieve4b: data.aiSpend?.sieve4b ?? { ...EMPTY_RUNPOD },
      validate32b: data.aiSpend?.validate32b ?? { ...EMPTY_RUNPOD },
      shortQwen: data.aiSpend?.shortQwen ?? { ...EMPTY_RUNPOD },
      loupeQwen: data.aiSpend?.loupeQwen ?? { ...EMPTY_RUNPOD },
      totalEstimatedCostUsd: data.aiSpend?.totalEstimatedCostUsd ?? 0,
      byStep: data.aiSpend?.byStep ?? [],
      weekly: (data.aiSpend?.weekly ?? []).map((w) => ({
        ...w,
        whisper: w.whisper ?? w.runpod ?? { ...EMPTY_RUNPOD },
        sieve4b: w.sieve4b ?? { ...EMPTY_RUNPOD },
        validate32b: w.validate32b ?? { ...EMPTY_RUNPOD },
        shortQwen: w.shortQwen ?? { ...EMPTY_RUNPOD },
        loupeQwen: w.loupeQwen ?? { ...EMPTY_RUNPOD },
      })),
      perContent: (data.aiSpend?.perContent ?? []).map((c) => ({
        ...c,
        whisper: c.whisper ?? { ...EMPTY_RUNPOD },
        sieve4b: c.sieve4b ?? { ...EMPTY_RUNPOD },
        validate32b: c.validate32b ?? { ...EMPTY_RUNPOD },
        shortQwen: c.shortQwen ?? { ...EMPTY_RUNPOD },
        loupeQwen: c.loupeQwen ?? { ...EMPTY_RUNPOD },
      })),
    },
    trackedReviewHoursByWeek: data.trackedReviewHoursByWeek ?? {},
  };
}

/** Monday (UTC) for an ISO week key — fallback when API omits weekStart. */
function isoWeekStartDate(weekKey: string): Date | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!m) return null;
  const isoYear = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (dow - 1));
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return monday;
}

function formatIsoWeekStart(weekKey: string, weekStart?: string): string {
  if (weekStart) {
    const d = new Date(`${weekStart}T12:00:00Z`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    }
  }
  const monday = isoWeekStartDate(weekKey);
  if (!monday || Number.isNaN(monday.getTime())) return weekKey;
  return monday.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** X-axis: week id on first line, Monday start date below. */
function WeekChartTick(props: {
  x?: number | string;
  y?: number | string;
  payload?: { value?: string };
  fill?: string;
  weekStarts?: Record<string, string>;
}) {
  const { x, y, payload, fill, weekStarts } = props;
  const weekKey = payload?.value ?? "";
  const start = formatIsoWeekStart(weekKey, weekStarts?.[weekKey]);
  const tx = typeof x === "number" ? x : Number(x) || 0;
  const ty = typeof y === "number" ? y : Number(y) || 0;
  return (
    <g transform={`translate(${tx},${ty})`}>
      <text x={0} y={0} dy={14} textAnchor="middle" fill={fill} fontSize={10}>
        {weekKey}
      </text>
      {start !== weekKey && (
        <text x={0} y={0} dy={28} textAnchor="middle" fill={fill} fontSize={10} opacity={0.75}>
          {start}
        </text>
      )}
    </g>
  );
}

/** Legend and content after the chart render later in the DOM and would paint over the tooltip. */
const TOOLTIP_WRAPPER_STYLE: React.CSSProperties = { zIndex: 1500, opacity: 1 };

/** Recharts default tooltip is light-only — match MUI paper/text for dark mode. */
function rechartsTooltipCursor(theme: Theme) {
  return { fill: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.08) };
}

type ChartTooltipPayload = {
  name?: string;
  value?: number | string | null;
  color?: string;
  payload?: Record<string, unknown>;
};

function ThemedChartTooltip({
  active,
  label,
  payload,
  theme,
  seriesColors,
  formatLabel,
  formatValue,
  sections,
  extraItems,
}: {
  active?: boolean;
  label?: unknown;
  payload?: readonly ChartTooltipPayload[];
  theme: Theme;
  seriesColors?: Record<string, string>;
  formatLabel?: (label: unknown) => React.ReactNode;
  formatValue?: (name: string, value: unknown, row: Record<string, unknown>) => string | null;
  /** When set, items are listed under these titled groups instead of a flat list. */
  sections?: Array<{ title: string; names: string[] }>;
  /** Rows that are not chart series (e.g. counts), built from the hovered data row. */
  extraItems?: (
    row: Record<string, unknown>,
  ) => Array<{ name: string; swatchColor: string; formatted: string }>;
}) {
  if (!active || !payload?.length) return null;

  const seriesItems = payload
    .map((entry) => {
      const name = String(entry.name ?? "");
      const row = entry.payload ?? {};
      const swatchColor =
        seriesColors?.[name] ??
        entry.color ??
        (typeof row.color === "string" ? row.color : undefined) ??
        theme.palette.text.disabled;
      const formatted = formatValue
        ? formatValue(name, entry.value, row)
        : entry.value == null
          ? null
          : String(entry.value);
      if (formatted == null) return null;
      return { name, swatchColor, formatted };
    })
    .filter((item): item is { name: string; swatchColor: string; formatted: string } => item != null);
  const items = [...seriesItems, ...(extraItems?.(payload[0]?.payload ?? {}) ?? [])];

  if (!items.length) return null;

  const displayLabel: React.ReactNode = formatLabel
    ? formatLabel(label)
    : label == null || label === ""
      ? null
      : typeof label === "string" || typeof label === "number"
        ? label
        : String(label);

  const byName = new Map(items.map((item) => [item.name, item]));
  const sectioned =
    sections?.map((section) => ({
      title: section.title,
      items: section.names
        .map((name) => byName.get(name))
        .filter((item): item is { name: string; swatchColor: string; formatted: string } => item != null),
    }))
      .filter((section) => section.items.length > 0) ?? null;
  const sectionedNames = new Set(sections?.flatMap((s) => s.names) ?? []);
  const ungrouped = sectioned
    ? items.filter((item) => !sectionedNames.has(item.name))
    : items;

  const renderItems = (list: Array<{ name: string; swatchColor: string; formatted: string }>) =>
    list.map((item) => (
      <Stack key={item.name} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25, minWidth: 170 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: item.name.includes("hours") ? 0.5 : "50%",
            bgcolor: item.swatchColor,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {item.name}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>
          {item.formatted}
        </Typography>
      </Stack>
    ));

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: theme.shadows[4],
        px: 1.25,
        py: 1,
      }}
    >
      {displayLabel != null && displayLabel !== "" && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, display: "block", mb: 0.75, color: "text.primary" }}
        >
          {displayLabel}
        </Typography>
      )}
      {sectioned
        ? (
            <>
              {sectioned.map((section, idx) => (
                <Box key={section.title} sx={{ mt: idx === 0 ? 0 : 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      fontSize: 10,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.25,
                    }}
                  >
                    {section.title}
                  </Typography>
                  {renderItems(section.items)}
                </Box>
              ))}
              {ungrouped.length > 0 && <Box sx={{ mt: 1 }}>{renderItems(ungrouped)}</Box>}
            </>
          )
        : renderItems(ungrouped)}
    </Box>
  );
}

const WEEKLY_TOOLTIP_SECTIONS = [
  {
    title: "Volume",
    names: ["Hours analyzed", "Mentions", "Accepted mentions"],
  },
  {
    title: "Performance",
    names: ["Precision", "Recall", "Edit rate", "Clean accept"],
  },
  {
    title: "Costs",
    names: [
      "GPT $",
      "Gemini $",
      "Whisper $",
      "Qwen 4B $",
      "Qwen 32B $",
      "Short Qwen $",
      "Loupe Qwen $",
      "AI cost",
      "Review hours",
      "Review time",
    ],
  },
  {
    title: "Per content hour by pipeline",
    names: [
      "Loupe · AI cost",
      "Loupe · review time",
      "Whisper only · AI cost",
      "Whisper only · review time",
    ],
  },
];

/** Chart series colors — not red/green (those mean improving/worsening on trend chips). */
function weeklySeriesColors(theme: Theme) {
  return {
    precision: theme.palette.primary.main,
    recall: "#0288D1",
    editRate: "#ED6C02",
    cleanAccept: "#7B1FA2",
    reviewHours: theme.palette.mode === "dark" ? "#546E7A" : "#B0BEC5",
    reviewHoursStroke: "#78909C",
    analyzedHours: theme.palette.mode === "dark" ? "#8D6E63" : "#D7CCC8",
    analyzedHoursStroke: "#8D6E63",
    gptCost: theme.palette.mode === "dark" ? "#90CAF9" : "#1565C0",
    geminiCost: theme.palette.mode === "dark" ? "#CE93D8" : "#7B1FA2",
    whisperCost: theme.palette.mode === "dark" ? "#66BB6A" : "#2E7D32",
    sieve4bCost: theme.palette.mode === "dark" ? "#81C784" : "#43A047",
    validate32bCost: theme.palette.mode === "dark" ? "#A5D6A7" : "#1B5E20",
    shortQwenCost: theme.palette.mode === "dark" ? "#FFAB91" : "#E65100",
    loupeQwenCost: theme.palette.mode === "dark" ? "#80DEEA" : "#00838F",
  };
}

const TREND_METRICS: Array<{
  key: keyof WeekRow;
  label: string;
  seriesKey: keyof ReturnType<typeof weeklySeriesColors>;
  /** Which direction counts as improvement. */
  goodDirection: "up" | "down";
}> = [
  { key: "precision", label: "Precision", seriesKey: "precision", goodDirection: "up" },
  { key: "recall", label: "Recall", seriesKey: "recall", goodDirection: "up" },
  { key: "editRate", label: "Edit rate", seriesKey: "editRate", goodDirection: "down" },
  { key: "cleanAcceptRate", label: "Clean accept", seriesKey: "cleanAccept", goodDirection: "up" },
];

/** First → last non-null delta (in percentage points) across the weekly series. */
function seriesDelta(
  weeks: WeekRow[],
  key: keyof WeekRow,
): { first: number | null; last: number | null; deltaPts: number | null } {
  const points = weeks
    .map((w) => w[key])
    .filter((v): v is number => typeof v === "number");
  if (points.length < 2) {
    const only = points[0] ?? null;
    return { first: only, last: only, deltaPts: null };
  }
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return { first, last, deltaPts: (last - first) * 100 };
}

function TrendChip({
  label,
  delta,
  goodDirection,
}: {
  label: string;
  delta: { first: number | null; last: number | null; deltaPts: number | null };
  goodDirection: "up" | "down";
}) {
  const theme = useTheme();
  const { last, deltaPts } = delta;
  let color = theme.palette.text.secondary;
  let arrow = "→";
  if (deltaPts != null && Math.abs(deltaPts) >= 0.05) {
    const goingUp = deltaPts > 0;
    arrow = goingUp ? "▲" : "▼";
    const improving = goingUp === (goodDirection === "up");
    color = improving ? theme.palette.success.main : theme.palette.error.main;
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150, flex: "1 1 150px" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {last == null ? "—" : `${(last * 100).toFixed(1)}%`}
      </Typography>
      <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
        {arrow}{" "}
        {deltaPts == null
          ? "no trend yet"
          : `${deltaPts > 0 ? "+" : ""}${deltaPts.toFixed(1)} pts vs first week`}
      </Typography>
    </Paper>
  );
}

export default function ReviewAuditAdmin() {
  const theme = useTheme();
  const [dateFrom, setDateFrom] = useState(ymdDaysAgo(DEFAULT_LOOKBACK_DAYS));
  const [dateTo, setDateTo] = useState(ymdDaysAgo(1));
  const [effortDateTo, setEffortDateTo] = useState(ymdDaysAgo(0));
  const [podcastId, setPodcastId] = useState("");
  const [applied, setApplied] = useState({
    dateFrom: ymdDaysAgo(DEFAULT_LOOKBACK_DAYS),
    dateTo: ymdDaysAgo(1),
    effortDateTo: ymdDaysAgo(0),
    podcastId: "",
  });
  const [hotspotSort, setHotspotSort] = useState<{ key: HotspotSortKey; direction: "asc" | "desc" }>({
    key: "total",
    direction: "desc",
  });
  const [expandedPodcastIds, setExpandedPodcastIds] = useState<Set<string>>(() => new Set());
  const [copySnack, setCopySnack] = useState<string | null>(null);

  const toggleHotspotSort = (key: HotspotSortKey) => {
    setHotspotSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "desc" ? "asc" : "desc" }
        : { key, direction: key === "title" ? "asc" : "desc" },
    );
  };

  const togglePodcastExpanded = (podcastId: string) => {
    setExpandedPodcastIds((prev) => {
      const next = new Set(prev);
      if (next.has(podcastId)) next.delete(podcastId);
      else next.add(podcastId);
      return next;
    });
  };

  const copyContentId = async (contentId: string) => {
    try {
      await navigator.clipboard.writeText(contentId);
      setCopySnack("Content ID copied");
    } catch {
      setCopySnack("Failed to copy content ID");
    }
  };

  const { data: rawData, isFetching, error } = useQuery<ReviewAuditSummary>({
    queryKey: ["reviewAudit", applied],
    queryFn: () =>
      apiService.getReviewAuditSummary({
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        effortDateTo: applied.effortDateTo,
        podcastId: applied.podcastId || undefined,
      }),
    staleTime: 60_000,
  });

  const data = useMemo(
    () => (rawData ? withReviewAuditDefaults(rawData) : undefined),
    [rawData],
  );

  const weekStartsByKey = useMemo(() => {
    if (!data) return {} as Record<string, string>;
    return Object.fromEntries(
      data.quality.weekly.map((w) => [w.week, w.weekStart ?? ""]),
    );
  }, [data]);

  const spendByWeek = useMemo(() => {
    if (!data?.aiSpend?.weekly) return {} as Record<string, NonNullable<ReviewAuditSummary["aiSpend"]>["weekly"][number]>;
    return Object.fromEntries(data.aiSpend.weekly.map((w) => [w.week, w]));
  }, [data]);

  const seriesColors = useMemo(() => weeklySeriesColors(theme), [theme]);
  const tooltipCursor = useMemo(() => rechartsTooltipCursor(theme), [theme]);

  const weeklyTooltipSeriesColors = useMemo(
    () => ({
      Precision: seriesColors.precision,
      Recall: seriesColors.recall,
      "Edit rate": seriesColors.editRate,
      "Clean accept": seriesColors.cleanAccept,
      "Review hours": seriesColors.reviewHoursStroke,
      "Hours analyzed": seriesColors.analyzedHoursStroke,
      "GPT $": seriesColors.gptCost,
      "Gemini $": seriesColors.geminiCost,
      "Whisper $": seriesColors.whisperCost,
      "Qwen 4B $": seriesColors.sieve4bCost,
      "Qwen 32B $": seriesColors.validate32bCost,
      "Short Qwen $": seriesColors.shortQwenCost,
      "Loupe Qwen $": seriesColors.loupeQwenCost,
    }),
    [seriesColors],
  );

  const weeklyChartData = useMemo(() => {
    if (!data) return [];
    const tracked = data.trackedReviewHoursByWeek ?? {};
    return data.quality.weekly.map((w) => {
      const spend = spendByWeek[w.week];
      const gptCostUsd = spend ? Number(spend.gpt.estimatedCostUsd.toFixed(3)) : null;
      const geminiCostUsd = spend ? Number(spend.gemini.estimatedCostUsd.toFixed(3)) : null;
      const whisperCostUsd = spend ? Number(spend.whisper.estimatedCostUsd.toFixed(3)) : null;
      const sieve4bCostUsd = spend ? Number(spend.sieve4b.estimatedCostUsd.toFixed(3)) : null;
      const validate32bCostUsd = spend
        ? Number(spend.validate32b.estimatedCostUsd.toFixed(3))
        : null;
      const shortQwenCostUsd = spend
        ? Number((spend.shortQwen?.estimatedCostUsd ?? 0).toFixed(3))
        : null;
      const loupeQwenCostUsd = spend
        ? Number((spend.loupeQwen?.estimatedCostUsd ?? 0).toFixed(3))
        : null;
      const aiCostUsd = spend ? Number(spend.totalEstimatedCostUsd.toFixed(3)) : null;
      const review = reviewHoursForWeek(w.week, tracked);
      return {
        week: w.week,
        weekStart: w.weekStart,
        reviewHours: review.hours,
        reviewHoursLabel: review.hours != null ? review.label : "",
        reviewHoursSource: review.source,
        analyzedHours: w.analyzedHours ?? 0,
        analyzedHoursLabel: w.analyzedHours ? formatAnalyzedHours(w.analyzedHours) : "",
        episodesAnalyzed: w.episodesAnalyzed ?? 0,
        mentions: w.detected,
        acceptedMentions: w.accepted + w.corrected,
        byPipeline: w.byPipeline,
        gptCostUsd: gptCostUsd ?? 0,
        geminiCostUsd: geminiCostUsd ?? 0,
        whisperCostUsd: whisperCostUsd ?? 0,
        sieve4bCostUsd: sieve4bCostUsd ?? 0,
        validate32bCostUsd: validate32bCostUsd ?? 0,
        shortQwenCostUsd: shortQwenCostUsd ?? 0,
        loupeQwenCostUsd: loupeQwenCostUsd ?? 0,
        aiCostUsd,
        aiCostLabel: aiCostUsd != null && aiCostUsd > 0 ? formatUsd(aiCostUsd) : "",
        Precision: w.precision == null ? null : Number((w.precision * 100).toFixed(1)),
        Recall: w.recall == null ? null : Number((w.recall * 100).toFixed(1)),
        "Edit rate": w.editRate == null ? null : Number((w.editRate * 100).toFixed(1)),
        "Clean accept":
          w.cleanAcceptRate == null ? null : Number((w.cleanAcceptRate * 100).toFixed(1)),
      };
    });
  }, [data, spendByWeek]);

  const aiCostAxisMax = useMemo(() => {
    const vals = weeklyChartData
      .map((r) => r.aiCostUsd)
      .filter((v): v is number => v != null && v > 0);
    if (vals.length === 0) return 1;
    const max = Math.max(...vals);
    if (max <= 1) return Math.ceil(max * 10) / 10 || 1;
    if (max <= 10) return Math.ceil(max);
    return Math.ceil(max / 5) * 5;
  }, [weeklyChartData]);

  const actionBuckets = useMemo(() => {
    if (!data) return [];
    const touched = data.effort.touched;
    return BUCKET_META.map((m) => {
      const count = data.effort.actions[m.key];
      return {
        ...m,
        count,
        share: touched > 0 ? count / touched : 0,
        color: m.color(theme),
      };
    });
  }, [data, theme]);

  const cleanShare = data && data.effort.touched > 0
    ? data.effort.actions.cleanAccept / data.effort.touched
    : 0;

  const sortedHotspotRows = useMemo((): HotspotRow[] => {
    if (!data) return [];
    const rows: HotspotRow[] = data.effort.perPodcast.map((p) => {
      const q = data.quality.perPodcast.find((x) => x.podcastId === p.podcastId);
      return { ...p, precision: q?.precision ?? null };
    });
    const mult = hotspotSort.direction === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (hotspotSort.key === "title") {
        return mult * a.title.localeCompare(b.title);
      }
      if (hotspotSort.key === "precision") {
        const av = a.precision ?? -1;
        const bv = b.precision ?? -1;
        return mult * (av - bv);
      }
      return mult * (a[hotspotSort.key] - b[hotspotSort.key]);
    });
    return rows;
  }, [data, hotspotSort]);

  const contentByPodcast = useMemo(() => {
    const map = new Map<string, HotspotContentRow[]>();
    if (!data) return map;
    const precisionById = new Map(
      data.quality.perContent.map((c) => [c.contentId, c.precision] as const),
    );
    const spendById = new Map(
      (data.aiSpend?.perContent ?? []).map((c) => [c.contentId, c] as const),
    );
    for (const c of data.effort.perContent) {
      const podcastKey = String(c.podcastId ?? "");
      if (!podcastKey || podcastKey === "unknown") continue;
      const spend = spendById.get(c.contentId);
      const row: HotspotContentRow = {
        contentId: c.contentId,
        title: c.title,
        rejected: c.rejected,
        added: c.added,
        edited: c.edited,
        cleanAccept: c.cleanAccept,
        total: c.total,
        precision: precisionById.get(c.contentId) ?? null,
        whisperCostUsd: spend ? spend.whisper.estimatedCostUsd : null,
        sieve4bCostUsd: spend ? spend.sieve4b.estimatedCostUsd : null,
        validate32bCostUsd: spend ? spend.validate32b.estimatedCostUsd : null,
        shortQwenCostUsd: spend ? spend.shortQwen.estimatedCostUsd : null,
        loupeQwenCostUsd: spend ? spend.loupeQwen.estimatedCostUsd : null,
        gptCostUsd: spend ? spend.gpt.estimatedCostUsd : null,
        geminiCostUsd: spend ? spend.gemini.estimatedCostUsd : null,
      };
      const list = map.get(podcastKey) ?? [];
      list.push(row);
      map.set(podcastKey, list);
    }
    return map;
  }, [data]);

  const runReport = () =>
    setApplied({ dateFrom, dateTo, effortDateTo, podcastId });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Mention review audit
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        Detection quality by publish week and review effort breakdown. Quality keys on episode publish date; effort
        keys on review date.
      </Typography>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }} flexWrap="wrap">
          <TextField
            label="Date from"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date to (publish)"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Effort date to (review)"
            type="date"
            size="small"
            value={effortDateTo}
            onChange={(e) => setEffortDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Podcast ID (optional)"
            size="small"
            value={podcastId}
            onChange={(e) => setPodcastId(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <Button variant="contained" onClick={runReport} disabled={isFetching}>
            {isFetching ? "Loading…" : "Run"}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load review audit. {(error as Error).message}
        </Alert>
      )}

      {isFetching && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <Stack spacing={3}>
          {/* Stat strip */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 2,
            }}
          >
            <StatCard value={data.effort.touched.toLocaleString()} label="Mentions touched" />
            <StatCard value={data.effort.episodesCompleted} label="Episodes review-ready" />
            <StatCard
              value={data.quality.missing.toLocaleString()}
              label="Missing (AI misses)"
              color={theme.palette.warning.dark}
              tooltip="Real mentions the AI failed to detect that a human had to add during review. This is the recall gap — high missing means the pipeline is under-detecting. Counted in the publish-date window."
            />
            <StatCard
              value={pct(data.quality.precision)}
              label="Precision"
              color={seriesColors.precision}
              tooltip="Of the mentions the AI proposed, the share that survived review (accepted or corrected, not rejected). Precision = (accepted + corrected) / (accepted + corrected + rejected). Low precision = too many false positives you have to reject."
            />
            <StatCard
              value={pct(data.quality.recall)}
              label="Recall"
              color={seriesColors.recall}
              tooltip="Of all real mentions found during review, the share the AI caught on its own. Recall = (accepted + corrected) / (accepted + corrected + missing), where 'missing' are mentions you had to add by hand. Low recall = the AI is missing real mentions."
            />
            <StatCard
              value={pct(cleanShare)}
              label="Clean-accept share"
              color={seriesColors.cleanAccept}
              tooltip="Of every mention you touched, the share you accepted with a single click — no reject, no edit, no add. Clean-accept share = clean accepts / mentions touched. This is the busywork an auto-accept could remove."
            />
          </Box>

          {/* Pipeline AI spend */}
          {data.aiSpend && (
            <Box>
              <SectionTitle caption="Estimated GPT + Gemini tokens and RunPod GPU cost by pod type (Whisper / Qwen 4B / Qwen 32B / Short Qwen / Loupe Qwen) for episodes in the publish (quality) window. Sums every completed pipeline run and transcription job (force-reruns count). 4B/32B are wall-clock × $/hr except the 2026-08-23 keep-pods run (24 A6000s × fleet hours). Loupe Qwen uses stored usdPerEpisode (wall × $/hr × pods). Pre-2026-08-07 GPU hours use legacy $0.44/hr.">
                Pipeline AI spend
              </SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 2,
                  mt: 1,
                }}
              >
                <StatCard
                  value={formatTokens(data.aiSpend.gpt.totalTokens)}
                  label="GPT tokens"
                  tooltip={`Prompt ${formatTokens(data.aiSpend.gpt.promptTokens)} · completion ${formatTokens(data.aiSpend.gpt.completionTokens)} · est. ${formatUsd(data.aiSpend.gpt.estimatedCostUsd)}`}
                />
                <StatCard
                  value={formatTokens(data.aiSpend.gemini.totalTokens)}
                  label="Gemini tokens"
                  tooltip={`Prompt ${formatTokens(data.aiSpend.gemini.promptTokens)} · completion ${formatTokens(data.aiSpend.gemini.completionTokens)} · est. ${formatUsd(data.aiSpend.gemini.estimatedCostUsd)}`}
                />
                <StatCard
                  value={formatProcessingDuration(data.aiSpend.whisper.processingDurationSeconds)}
                  label="Whisper (ASR)"
                  tooltip={`${formatGpuHours(data.aiSpend.whisper.gpuHours)} · est. ${formatUsd(data.aiSpend.whisper.estimatedCostUsd)}. Pre-2026-08-07 uses legacy $0.44/GPU-hr; after cutover uses stored pricePerHour (L4).`}
                />
                <StatCard
                  value={formatProcessingDuration(data.aiSpend.sieve4b.processingDurationSeconds)}
                  label="Qwen 4B"
                  tooltip={`${formatGpuHours(data.aiSpend.sieve4b.gpuHours)} · est. ${formatUsd(data.aiSpend.sieve4b.estimatedCostUsd)} (A6000 wall-clock × $/hr; 2026-08-23 run uses 12 pods × fleet hours).`}
                />
                <StatCard
                  value={formatProcessingDuration(data.aiSpend.validate32b.processingDurationSeconds)}
                  label="Qwen 32B"
                  tooltip={`${formatGpuHours(data.aiSpend.validate32b.gpuHours)} · est. ${formatUsd(data.aiSpend.validate32b.estimatedCostUsd)} (A6000 wall-clock × $/hr; 2026-08-23 run uses 12 pods × fleet hours).`}
                />
                <StatCard
                  value={formatProcessingDuration(data.aiSpend.shortQwen.processingDurationSeconds)}
                  label="Short Qwen"
                  tooltip={`${formatGpuHours(data.aiSpend.shortQwen.gpuHours)} · est. ${formatUsd(data.aiSpend.shortQwen.estimatedCostUsd)} (company short Qwen3-VL RunPod).`}
                />
                <StatCard
                  value={formatProcessingDuration(data.aiSpend.loupeQwen.processingDurationSeconds)}
                  label="Loupe Qwen"
                  tooltip={`${formatGpuHours(data.aiSpend.loupeQwen.gpuHours)} · est. ${formatUsd(data.aiSpend.loupeQwen.estimatedCostUsd)} (Loupe sieve+cue+segment RunPod; wall × pods × $/hr).`}
                />
                <StatCard
                  value={formatUsd(data.aiSpend.totalEstimatedCostUsd)}
                  label="Total est. USD"
                  tooltip="Sum of GPT + Gemini + Whisper + Qwen 4B + Qwen 32B + Short Qwen + Loupe Qwen estimates across all completed runs and transcription jobs for quality-window episodes (reruns included). Only the 2026-08-23 VLM run uses 24-pod fleet hours."
                />
              </Box>
              {data.aiSpend.episodesWithUsage < data.aiSpend.episodesInQuality && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Spend found for {data.aiSpend.episodesWithUsage} of{" "}
                  {data.aiSpend.episodesInQuality} quality-window episodes
                  {data.aiSpend.episodesInQuality - data.aiSpend.episodesWithUsage > 0
                    ? ` (${data.aiSpend.episodesInQuality - data.aiSpend.episodesWithUsage} with no completed runs or transcription jobs)`
                    : ""}
                  .
                </Alert>
              )}
              {data.aiSpend.byStep.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pipeline step</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Est. USD</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.aiSpend.byStep.map((s) => (
                        <TableRow key={s.scriptVersion}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                              {s.scriptVersion}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatTokens(s.totalTokens)}</TableCell>
                          <TableCell align="right">{formatUsd(s.estimatedCostUsd)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Weekly trend */}
          <Box>
            <SectionTitle caption="Detection quality per publish week. Brown bars = hours of content analyzed (review-ready episodes); grey bars = review-time estimates; stacked bars = GPT / Gemini / Whisper / Qwen 4B / Qwen 32B / Short Qwen / Loupe Qwen cost (est. $, right axis).">
              Weekly trend (by publish date)
            </SectionTitle>
            {data.quality.weekly.length < 2 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Need at least two publish weeks in the window to show a trend. Widen the date range above.
              </Alert>
            ) : (
              <>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
                  {TREND_METRICS.map((m) => (
                    <TrendChip
                      key={String(m.key)}
                      label={m.label}
                      delta={seriesDelta(data.quality.weekly, m.key)}
                      goodDirection={m.goodDirection}
                    />
                  ))}
                </Stack>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <ResponsiveContainer width="100%" height={340}>
                    <ComposedChart
                      data={weeklyChartData}
                      margin={{ left: 10, right: 56, top: 10, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} yAxisId="pct" />
                      <XAxis
                        dataKey="week"
                        height={56}
                        interval={0}
                        tick={(props) => (
                          <WeekChartTick
                            {...props}
                            fill={theme.palette.text.secondary}
                            weekStarts={weekStartsByKey}
                          />
                        )}
                      />
                      <YAxis
                        yAxisId="pct"
                        tick={{ fontSize: 11 }}
                        unit="%"
                        domain={[0, 100]}
                        label={{ value: "Quality %", angle: -90, position: "insideLeft", fontSize: 11 }}
                      />
                      <YAxis
                        yAxisId="analyzed"
                        hide
                        domain={[0, (dataMax: number) => Math.max(1, dataMax * 1.15)]}
                      />
                      <YAxis
                        yAxisId="usd"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        domain={[0, aiCostAxisMax]}
                        tickFormatter={(v) => (v >= 1 ? `$${v}` : `$${Number(v).toFixed(2)}`)}
                        label={{ value: "AI cost $", angle: 90, position: "insideRight", fontSize: 11 }}
                      />
                      <Tooltip
                        cursor={tooltipCursor}
                        wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                        content={(props) => (
                          <ThemedChartTooltip
                            {...props}
                            theme={theme}
                            seriesColors={weeklyTooltipSeriesColors}
                            sections={WEEKLY_TOOLTIP_SECTIONS}
                            extraItems={(row) => {
                              const contentHours = Number(row.analyzedHours) || 0;
                              const aiCost = Number(row.aiCostUsd) || 0;
                              const reviewHours = Number(row.reviewHours) || 0;
                              return [
                                {
                                  name: "Mentions",
                                  swatchColor: theme.palette.text.secondary,
                                  formatted: String(Number(row.mentions) || 0),
                                },
                                {
                                  name: "Accepted mentions",
                                  swatchColor: theme.palette.success.main,
                                  formatted: String(Number(row.acceptedMentions) || 0),
                                },
                                ...(contentHours > 0 && aiCost > 0
                                  ? [
                                      {
                                        name: "AI cost",
                                        swatchColor: seriesColors.analyzedHoursStroke,
                                        formatted: `${formatUsd(aiCost / contentHours)} per content h`,
                                      },
                                    ]
                                  : []),
                                ...(contentHours > 0 && reviewHours > 0
                                  ? [
                                      {
                                        name: "Review time",
                                        swatchColor: seriesColors.reviewHoursStroke,
                                        formatted: `${((reviewHours * 60) / contentHours).toFixed(1)} min per content h`,
                                      },
                                    ]
                                  : []),
                                ...pipelinePerHourItems(
                                  row.byPipeline as WeekRow["byPipeline"],
                                  reviewHours,
                                  {
                                    loupe: seriesColors.loupeQwenCost,
                                    whisper: seriesColors.whisperCost,
                                  },
                                ),
                              ];
                            }}
                            formatLabel={(week) => {
                              const key = String(week ?? "");
                              const start = formatIsoWeekStart(key, weekStartsByKey[key]);
                              return start === key ? key : `${key} · ${start}`;
                            }}
                            formatValue={(name, value, row) => {
                              if (name === "Hours analyzed") {
                                if (value == null || Number(value) <= 0) return null;
                                const eps = Number(row.episodesAnalyzed) || 0;
                                return `${formatAnalyzedHours(Number(value))} · ${eps} ep`;
                              }
                              if (name === "Review hours") {
                                if (value == null) return null;
                                const hrsLabel = row.reviewHoursLabel as string | undefined;
                                const source = row.reviewHoursSource as string | null | undefined;
                                const base =
                                  hrsLabel && hrsLabel !== "—" ? hrsLabel : `${value} h`;
                                if (source === "fixed") return `${base} (est.)`;
                                if (source === "tracked") return `${base} (tracked)`;
                                return base;
                              }
                              if (
                                name === "GPT $" ||
                                name === "Gemini $" ||
                                name === "Whisper $" ||
                                name === "Qwen 4B $" ||
                                name === "Qwen 32B $" ||
                                name === "Short Qwen $" ||
                                name === "Loupe Qwen $" ||
                                name === "RunPod $"
                              ) {
                                if (value == null || Number(value) <= 0) return null;
                                return formatUsd(Number(value));
                              }
                              return value == null ? null : `${value}%`;
                            }}
                          />
                        )}
                      />
                      <Legend />
                      <Bar
                        yAxisId="analyzed"
                        dataKey="analyzedHours"
                        name="Hours analyzed"
                        fill={seriesColors.analyzedHours}
                        fillOpacity={0.6}
                        stroke={seriesColors.analyzedHoursStroke}
                        strokeWidth={1}
                        radius={[4, 4, 0, 0]}
                        barSize={22}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey="analyzedHoursLabel"
                          position="top"
                          fontSize={10}
                          fill={theme.palette.text.secondary}
                        />
                      </Bar>
                      <Bar
                        yAxisId="analyzed"
                        dataKey="reviewHours"
                        name="Review hours"
                        fill={seriesColors.reviewHours}
                        fillOpacity={0.55}
                        stroke={seriesColors.reviewHoursStroke}
                        strokeWidth={1}
                        radius={[4, 4, 0, 0]}
                        barSize={22}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey="reviewHoursLabel"
                          position="top"
                          fontSize={10}
                          fill={theme.palette.text.secondary}
                        />
                      </Bar>
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="gptCostUsd"
                        name="GPT $"
                        fill={seriesColors.gptCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="geminiCostUsd"
                        name="Gemini $"
                        fill={seriesColors.geminiCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="whisperCostUsd"
                        name="Whisper $"
                        fill={seriesColors.whisperCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="sieve4bCostUsd"
                        name="Qwen 4B $"
                        fill={seriesColors.sieve4bCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="validate32bCostUsd"
                        name="Qwen 32B $"
                        fill={seriesColors.validate32bCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="shortQwenCostUsd"
                        name="Short Qwen $"
                        fill={seriesColors.shortQwenCost}
                        fillOpacity={0.85}
                        barSize={22}
                        isAnimationActive={false}
                      />
                      <Bar
                        yAxisId="usd"
                        stackId="aiCost"
                        dataKey="loupeQwenCostUsd"
                        name="Loupe Qwen $"
                        fill={seriesColors.loupeQwenCost}
                        fillOpacity={0.85}
                        radius={[4, 4, 0, 0]}
                        barSize={22}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey="aiCostLabel"
                          position="top"
                          fontSize={10}
                          fill={theme.palette.text.secondary}
                        />
                      </Bar>
                      <Line yAxisId="pct" type="monotone" dataKey="Precision" stroke={seriesColors.precision} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      <Line yAxisId="pct" type="monotone" dataKey="Recall" stroke={seriesColors.recall} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      <Line yAxisId="pct" type="monotone" dataKey="Edit rate" stroke={seriesColors.editRate} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      <Line yAxisId="pct" type="monotone" dataKey="Clean accept" stroke={seriesColors.cleanAccept} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell rowSpan={2} sx={{ verticalAlign: "bottom" }}>
                            Week
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={8}
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                              color: "text.secondary",
                              borderBottom: 0,
                            }}
                          >
                            Performance
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={11}
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                              color: "text.secondary",
                              borderBottom: 0,
                              borderLeft: "1px solid",
                              borderLeftColor: "divider",
                            }}
                          >
                            Costs
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="right">Proposed</TableCell>
                          <TableCell align="right">Missing</TableCell>
                          <TableCell align="right">Episodes</TableCell>
                          <TableCell align="right">Hours analyzed</TableCell>
                          <TableCell align="right">Precision</TableCell>
                          <TableCell align="right">Recall</TableCell>
                          <TableCell align="right">Edit rate</TableCell>
                          <TableCell align="right">Clean accept</TableCell>
                          <TableCell align="right" sx={{ borderLeft: "1px solid", borderLeftColor: "divider" }}>
                            GPT tok
                          </TableCell>
                          <TableCell align="right">GPT $</TableCell>
                          <TableCell align="right">Gemini tok</TableCell>
                          <TableCell align="right">Gemini $</TableCell>
                          <TableCell align="right">Whisper $</TableCell>
                          <TableCell align="right">Qwen 4B $</TableCell>
                          <TableCell align="right">Qwen 32B $</TableCell>
                          <TableCell align="right">Short Qwen $</TableCell>
                          <TableCell align="right">Loupe Qwen $</TableCell>
                          <TableCell align="right">AI total $</TableCell>
                          <TableCell align="right">Review time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.quality.weekly.map((w) => {
                          const spend = spendByWeek[w.week];
                          return (
                            <TableRow key={w.week}>
                              <TableCell>
                                <Typography variant="body2">{w.week}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatIsoWeekStart(w.week, w.weekStart)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{w.detected}</TableCell>
                              <TableCell align="right">{w.missing}</TableCell>
                              <TableCell align="right">{w.episodesReviewed}</TableCell>
                              <TableCell align="right">
                                {w.analyzedHours ? formatAnalyzedHours(w.analyzedHours) : "—"}
                              </TableCell>
                              <TableCell align="right">{pct(w.precision)}</TableCell>
                              <TableCell align="right">{pct(w.recall)}</TableCell>
                              <TableCell align="right">{pct(w.editRate)}</TableCell>
                              <TableCell align="right">{pct(w.cleanAcceptRate)}</TableCell>
                              <TableCell align="right" sx={{ borderLeft: "1px solid", borderLeftColor: "divider" }}>
                                {spend ? formatTokens(spend.gpt.totalTokens) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.gpt.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatTokens(spend.gemini.totalTokens) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.gemini.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.whisper.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.sieve4b.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.validate32b.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.shortQwen.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.loupeQwen.estimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {spend ? formatUsd(spend.totalEstimatedCostUsd) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {reviewHoursForWeek(w.week, data.trackedReviewHoursByWeek).label}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
          </Box>

          <ReviewTimeByPodcastSection />

          {/* Where time goes */}
          <Box>
            <SectionTitle caption="Review actions in the effort window — share of mentions touched.">
              Where the time goes
            </SectionTitle>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mt: 1,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    layout="vertical"
                    data={actionBuckets.map((b) => ({ name: b.label, count: b.count, color: b.color }))}
                    margin={{ left: 20, right: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip
                      cursor={tooltipCursor}
                      wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                      content={(props) => (
                        <ThemedChartTooltip
                          {...props}
                          theme={theme}
                          formatValue={(_name, value) => (value == null ? null : String(value))}
                        />
                      )}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="count" position="right" />
                      {actionBuckets.map((b) => (
                        <Cell key={b.key} fill={b.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bucket</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actionBuckets.map((b) => (
                      <TableRow key={b.key}>
                        <TableCell>
                          <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", bgcolor: b.color, mr: 1 }} />
                          {b.label}
                        </TableCell>
                        <TableCell align="right">{b.count.toLocaleString()}</TableCell>
                        <TableCell align="right">{pct(b.share)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Detection quality by flow */}
          <Box>
            <SectionTitle caption="False-positive rate = rejected / proposed. Attack the worst flows first.">
              Detection quality by flow
            </SectionTitle>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mt: 1,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={data.quality.flows.map((f) => ({
                      name: f.label,
                      fp: f.fpRate == null ? 0 : Number((f.fpRate * 100).toFixed(1)),
                    }))}
                    margin={{ left: 10, right: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip
                      cursor={tooltipCursor}
                      wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                      content={(props) => (
                        <ThemedChartTooltip
                          {...props}
                          theme={theme}
                          seriesColors={{ fp: theme.palette.error.main }}
                          formatValue={(_name, value) => (value == null ? null : `${value}%`)}
                        />
                      )}
                    />
                    <Bar dataKey="fp" fill={theme.palette.error.main} radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="fp" position="top" formatter={(v: any) => `${v}%`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Flow</TableCell>
                      <TableCell align="right">Proposed</TableCell>
                      <TableCell align="right">Rejected</TableCell>
                      <TableCell align="right">FP%</TableCell>
                      <TableCell>Top reject reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.quality.flows.map((f) => (
                      <TableRow key={f.flow}>
                        <TableCell>{f.label}</TableCell>
                        <TableCell align="right">{f.proposed}</TableCell>
                        <TableCell align="right">{f.rejected}</TableCell>
                        <TableCell align="right">{pct(f.fpRate)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {f.topRejectReasons.map((r) => `${r.key} (${r.count})`).join(", ") || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Leverage + edited fields */}
          <Box>
            <SectionTitle caption="Total corrections + rejections attributable to each failure mode (quality window).">
              Error mass — leverage ranking
            </SectionTitle>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
                gap: 3,
                mt: 1,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2 }}>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    layout="vertical"
                    data={data.quality.leverage.slice(0, 10).map((l) => ({ name: l.label, count: l.count }))}
                    margin={{ left: 20, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 10 }} />
                    <Tooltip
                      cursor={tooltipCursor}
                      wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                      content={(props) => (
                        <ThemedChartTooltip
                          {...props}
                          theme={theme}
                          seriesColors={{ count: theme.palette.primary.main }}
                          formatValue={(_name, value) => (value == null ? null : String(value))}
                        />
                      )}
                    />
                    <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="count" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <TableContainer component={Paper} variant="outlined" sx={{ alignSelf: "start" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Edited field (effort)</TableCell>
                      <TableCell align="right">Mentions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.effort.editedFields.map((e) => (
                      <TableRow key={e.key}>
                        <TableCell>{e.key}</TableCell>
                        <TableCell align="right">{e.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Reject reasons */}
          {data.effort.rejectReasons.length > 0 && (
            <Box>
              <SectionTitle caption="Raw reject reasons over the review window — deterministic suppression targets.">
                Top reject reasons
              </SectionTitle>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {data.effort.rejectReasons.map((r) => (
                  <Chip key={r.key} label={`${r.key} · ${r.count}`} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          {/* Per-podcast hotspots */}
          <Box>
            <SectionTitle caption="Concentrations a single fix can clear. Click a column header to sort. Expand a podcast for its top 10 worst episodes by the sorted column.">
              Podcast hotspots
            </SectionTitle>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width={40} />
                    <TableCell>
                      <TableSortLabel
                        active={hotspotSort.key === "title"}
                        direction={hotspotSort.key === "title" ? hotspotSort.direction : "asc"}
                        onClick={() => toggleHotspotSort("title")}
                      >
                        Podcast
                      </TableSortLabel>
                    </TableCell>
                    {(
                      [
                        ["total", "Touched"],
                        ["rejected", "Rejects"],
                        ["edited", "Edits"],
                        ["added", "Adds"],
                        ["cleanAccept", "Clean"],
                        ["precision", "Precision"],
                      ] as const
                    ).map(([key, label]) => (
                      <TableCell key={key} align="right">
                        <TableSortLabel
                          active={hotspotSort.key === key}
                          direction={hotspotSort.key === key ? hotspotSort.direction : "desc"}
                          onClick={() => toggleHotspotSort(key)}
                          sx={{
                            flexDirection: "row-reverse",
                            "& .MuiTableSortLabel-icon": { marginRight: 0, marginLeft: 0.5 },
                          }}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedHotspotRows.map((p) => {
                    const open = expandedPodcastIds.has(p.podcastId);
                    const worst = topWorstContentsForPodcast(
                      contentByPodcast.get(p.podcastId) ?? [],
                      hotspotSort.key,
                      10,
                    );
                    return (
                      <React.Fragment key={p.podcastId}>
                        <TableRow
                          hover
                          onClick={() => togglePodcastExpanded(p.podcastId)}
                          sx={{ cursor: "pointer", "& > *": { borderBottom: open ? "unset" : undefined } }}
                        >
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-label={open ? "Collapse podcast" : "Expand podcast"}
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePodcastExpanded(p.podcastId);
                              }}
                            >
                              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{p.title}</TableCell>
                          <TableCell align="right">{p.total}</TableCell>
                          <TableCell align="right">{p.rejected}</TableCell>
                          <TableCell align="right">{p.edited}</TableCell>
                          <TableCell align="right">{p.added}</TableCell>
                          <TableCell align="right">{p.cleanAccept}</TableCell>
                          <TableCell align="right">{pct(p.precision)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ mx: 1, my: 1.5 }}>
                                {worst.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    {data.effort.perContent.length === 0
                                      ? "No episode breakdown in this response — click Run to refresh after the backend reloads."
                                      : "No episode-level reviews in this window."}
                                  </Typography>
                                ) : (
                                  <Table size="small" sx={{ bgcolor: alpha(theme.palette.action.hover, 0.35) }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Episode</TableCell>
                                        <TableCell align="right">Touched</TableCell>
                                        <TableCell align="right">Rejects</TableCell>
                                        <TableCell align="right">Edits</TableCell>
                                        <TableCell align="right">Adds</TableCell>
                                        <TableCell align="right">Clean</TableCell>
                                        <TableCell align="right">Precision</TableCell>
                                        <TableCell align="right">Whisper $</TableCell>
                                        <TableCell align="right">Qwen 4B $</TableCell>
                                        <TableCell align="right">Qwen 32B $</TableCell>
                                        <TableCell align="right">Short Qwen $</TableCell>
                                        <TableCell align="right">Loupe Qwen $</TableCell>
                                        <TableCell align="right">GPT $</TableCell>
                                        <TableCell align="right">Gemini $</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {worst.map((c) => (
                                        <TableRow key={c.contentId}>
                                          <TableCell sx={{ maxWidth: 360 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  flex: 1,
                                                  minWidth: 0,
                                                }}
                                                title={c.title}
                                              >
                                                {c.title}
                                              </Typography>
                                              <MuiTooltip title="Copy content ID">
                                                <IconButton
                                                  size="small"
                                                  aria-label="Copy content ID"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void copyContentId(c.contentId);
                                                  }}
                                                >
                                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </MuiTooltip>
                                            </Stack>
                                          </TableCell>
                                          <TableCell align="right">{c.total}</TableCell>
                                          <TableCell align="right">{c.rejected}</TableCell>
                                          <TableCell align="right">{c.edited}</TableCell>
                                          <TableCell align="right">{c.added}</TableCell>
                                          <TableCell align="right">{c.cleanAccept}</TableCell>
                                          <TableCell align="right">{pct(c.precision)}</TableCell>
                                          <TableCell align="right">
                                            {c.whisperCostUsd != null ? formatUsd(c.whisperCostUsd) : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.sieve4bCostUsd != null ? formatUsd(c.sieve4bCostUsd) : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.validate32bCostUsd != null
                                              ? formatUsd(c.validate32bCostUsd)
                                              : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.shortQwenCostUsd != null
                                              ? formatUsd(c.shortQwenCostUsd)
                                              : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.loupeQwenCostUsd != null
                                              ? formatUsd(c.loupeQwenCostUsd)
                                              : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.gptCostUsd != null ? formatUsd(c.gptCostUsd) : "—"}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.geminiCostUsd != null ? formatUsd(c.geminiCostUsd) : "—"}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Quality window {data.windows.quality.dateFrom} → {data.windows.quality.dateTo} · effort window{" "}
            {data.windows.effort.dateFrom} → {data.windows.effort.dateTo} · weekly review hours use the /review
            timer when available, otherwise fixed manual estimates for older weeks.
          </Typography>
        </Stack>
      )}

      <Snackbar
        open={copySnack != null}
        autoHideDuration={2000}
        onClose={() => setCopySnack(null)}
        message={copySnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}
